import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { dataFetch } from '../../api';
import ModulePlayerModal from '../../components/learner/ModulePlayerModal';
import { incidentEvents } from '../../data/incidentEvents';
import { useLocation, useNavigate } from 'react-router-dom';
import { generateCertificate } from '../../utils/certificateUtils';

const { FiBookOpen, FiCheckCircle, FiTarget, FiAward, FiPlay, FiChevronDown, FiChevronUp, FiLoader, FiDownload } = FiIcons;

// Helper to safely extract JSON arrays returned from the DB
const getArray = (val) => {
  if (Array.isArray(val)) return val;
  if (!val) return [];
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      if (val.includes(',')) return val.split(',').map(s => s.trim());
      return [val];
    }
  }
  return [val];
};

const CampaignCard = ({ campaign, allModules, assignments, onPlayModule, learnerName }) => {
  const [expanded, setExpanded] = useState(false);
  
  const campModuleIds = getArray(campaign.trainingContent).map(String);
  const campModules = allModules.filter(m => campModuleIds.includes(String(m.id)));
  
  const completedCount = campModules.filter(m => {
    const ass = assignments.find(a => String(a.campaign_id) === String(campaign.id) && String(a.module_id) === String(m.id) && a.status === 'Completed');
    if (!ass) return false;
    // Check if the completion has expired
    if (ass.expires_at && new Date(ass.expires_at) < new Date()) return false;
    return true;
  }).length;

  const pct = campModules.length ? Math.round((completedCount / campModules.length) * 100) : 0;
  const status = pct === 100 ? 'Completed' : pct > 0 ? 'In Progress' : 'Not Started';

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-4">
      <div className="flex items-start justify-between gap-3 p-4 cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => setExpanded(!expanded)}>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800">{campaign.campaignName}</p>
          <p className="text-xs text-slate-500 mt-0.5">Due: {campaign.endDate} · {campaign.campaignType}</p>
          <div className="flex items-center gap-2 mt-2">
            <div className="flex-1 bg-slate-100 rounded-full h-1.5 max-w-[200px]">
              <div className={`h-1.5 rounded-full ${pct === 100 ? 'bg-emerald-500' : pct >= 50 ? 'bg-blue-500' : 'bg-orange-400'}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs font-bold text-slate-600">{pct}%</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${pct === 100 ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : pct > 0 ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
            {status}
          </span>
          <SafeIcon icon={expanded ? FiChevronUp : FiChevronDown} className="text-slate-400 mt-1" />
        </div>
      </div>
      
      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50 p-4 space-y-2">
          {campModules.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-2">No modules in this campaign.</p>
          ) : (
            campModules.map(m => {
              const ass = assignments.find(a => String(a.campaign_id) === String(campaign.id) && String(a.module_id) === String(m.id));
              const isCompleted = ass?.status === 'Completed';
              const passed = isCompleted && ass?.score >= 80;
              const expiresAt = ass?.expires_at ? new Date(ass.expires_at) : null;
              const isExpired = expiresAt && expiresAt < new Date();
              const isValidCompletion = isCompleted && !isExpired;

              return (
                <div key={m.id} className="flex items-center justify-between bg-white border border-slate-200 p-3 rounded-lg shadow-sm">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{m.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-slate-500">{m.deliveryFormat} · {m.duration} mins.</span>
                      {isValidCompletion && (
                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                          Score: {ass.score}%
                        </span>
                      )}
                      {isCompleted && isExpired && (
                        <span className="text-[10px] font-bold text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded border border-orange-200">
                          Expired (Renewal Required)
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {passed && isValidCompletion && (
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          generateCertificate(learnerName, m.title, new Date(ass.completed_at || Date.now()).toLocaleDateString());
                        }}
                        className="text-blue-600 bg-blue-50 border border-blue-200 hover:bg-blue-100 p-1.5 rounded-lg transition-colors flex items-center justify-center"
                        title="Download Certificate"
                      >
                        <SafeIcon icon={FiDownload} className="text-sm" />
                      </button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); onPlayModule(campaign, m, ass); }} 
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${isValidCompletion ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'}`}>
                      <SafeIcon icon={isValidCompletion ? FiCheckCircle : FiPlay} className={isValidCompletion ? 'text-emerald-500' : ''} /> 
                      {isValidCompletion ? 'Review' : isExpired ? 'Renew' : 'Start'}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

const EmployeePortal = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  const [employee, setEmployee] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [modules, setModules] = useState([]);
  const [assignments, setAssignments] = useState([]);
  
  const [playSession, setPlaySession] = useState(null);
  const [reportedSims, setReportedSims] = useState([]);
  
  const isHistoryTab = location.pathname.includes('my-history');
  const history = incidentEvents.filter(e => e.learnerId === (currentUser.learnerId || 'L001'));

  useEffect(() => {
    const load = async () => {
      try {
        let empRes = await dataFetch(`read/employees?user_id=${currentUser.id}`);
        let emp = empRes?.data?.[0];

        // Fallback: If user_id isn't linked yet, search by email and link it
        if (!emp && currentUser.email) {
          const emailRes = await dataFetch(`read/employees?email=${encodeURIComponent(currentUser.email)}`);
          emp = emailRes?.data?.[0];
          
          if (emp) {
            // Silently link the user_id to the employee profile so future queries load instantly
            dataFetch(`update/employees/${emp.id}`, {
              method: 'PUT',
              body: JSON.stringify({ ...emp, user_id: currentUser.id })
            }).catch(e => console.warn('Failed to sync employee user_id', e));
          }
        }

        if (!emp) { 
          setLoading(false); 
          return; 
        }
        setEmployee(emp);

        const campRes = await dataFetch('read/campaigns');
        const allCamps = campRes?.data || [];
        
        const myCamps = allCamps.filter(c => {
          // Check explicit learner assignment
          const learners = getArray(c.assignedLearners).map(String);
          const isExplicit = learners.includes(String(emp.id));
          
          // Check department-based assignment
          const deptArray = c.audience && c.audience !== 'Custom' ? c.audience.split(',').map(s => s.trim()) : [];
          const isDept = emp.department && deptArray.includes(emp.department);
          
          return isExplicit || isDept;
        });

        const modRes = await dataFetch('read/modules');
        const allMods = modRes?.data || [];

        const assRes = await dataFetch(`read/assignments?employee_id=${emp.id}`);
        const myAss = assRes?.data || [];

        setCampaigns(myCamps);
        setModules(allMods);
        setAssignments(myAss);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser.id, currentUser.email]);

  const handleModuleComplete = (newAssignment) => {
    setAssignments(prev => {
       const exists = prev.findIndex(a => a.id === newAssignment.id);
       if (exists >= 0) {
         const next = [...prev];
         next[exists] = newAssignment;
         return next;
       }
       return [...prev, newAssignment];
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <SafeIcon icon={FiLoader} className="animate-spin text-3xl" />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-20 bg-white rounded-2xl border border-slate-200 text-slate-500 shadow-sm">
        <p className="font-semibold text-slate-700">No employee profile found for your account.</p>
        <p className="text-sm mt-2">Please contact your administrator to get assigned.</p>
      </div>
    );
  }

  const completed = campaigns.filter(c => {
     const cModIds = getArray(c.trainingContent).map(String);
     if (cModIds.length === 0) return false;
     
     const cMods = modules.filter(m => cModIds.includes(String(m.id)));
     if (cMods.length === 0) return false;

     const cAss = assignments.filter(a => String(a.campaign_id) === String(c.id) && a.status === 'Completed' && (!a.expires_at || new Date(a.expires_at) >= new Date()));
     return cMods.length === cAss.length;
  }).length;

  const notStarted = campaigns.filter(c => {
     const cModIds = getArray(c.trainingContent).map(String);
     if (cModIds.length === 0) return true;
     
     const cMods = modules.filter(m => cModIds.includes(String(m.id)));
     const cAss = assignments.filter(a => String(a.campaign_id) === String(c.id) && a.status === 'Completed' && (!a.expires_at || new Date(a.expires_at) >= new Date()));
     return cAss.length === 0;
  }).length;

  const inProgress = campaigns.length - completed - notStarted;

  // Process completed assignments to extract unique badges based on the highest score per module
  const completedAssignments = assignments.filter(a => a.status === 'Completed' && (!a.expires_at || new Date(a.expires_at) >= new Date()));
  const moduleScores = {};
  completedAssignments.forEach(a => {
    const modId = String(a.module_id);
    if (!moduleScores[modId] || a.score > moduleScores[modId].score) {
      moduleScores[modId] = a;
    }
  });
  const badgeAssignments = Object.values(moduleScores);
  const learnerFullName = `${employee.firstName || ''} ${employee.lastName || ''}`.trim();

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="bg-[#0A1128] rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center text-2xl font-bold backdrop-blur-sm">
            {currentUser.avatar}
          </div>
          <div>
            <h2 className="text-xl font-bold">Welcome back, {currentUser.name.split(' ')[0]}!</h2>
            <p className="text-white text-sm mt-0.5">{employee.department || 'General'} · {employee.jobTitle || currentUser.role}</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-6">
          {[
            { label: 'Completed', value: completed, color: 'text-emerald-400' },
            { label: 'In Progress', value: Math.max(0, inProgress), color: 'text-amber-400' },
            { label: 'Not Started', value: Math.max(0, notStarted), color: 'text-red-400' },
          ].map(s => (
            <div key={s.label} className="bg-white/5 rounded-xl p-3 text-center backdrop-blur-sm border border-white/10">
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-white mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex border-b border-slate-200 mb-6">
        <button 
          onClick={() => navigate('/employee/dashboard')}
          className={`pb-3 px-4 font-semibold text-sm border-b-2 transition-colors ${!isHistoryTab ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          Dashboard
        </button>
        <button 
          onClick={() => navigate('/employee/my-history')}
          className={`pb-3 px-4 font-semibold text-sm border-b-2 transition-colors ${isHistoryTab ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          My History & Badges
        </button>
      </div>

      {!isHistoryTab && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <SafeIcon icon={FiBookOpen} className="text-blue-500" /> Assigned Training
          </h3>
          {campaigns.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400 shadow-sm">
              <SafeIcon icon={FiAward} className="text-4xl mx-auto mb-2 opacity-30" />
              <p>No training assigned yet.</p>
            </div>
          ) : (
            campaigns.map(c => (
              <CampaignCard 
                key={c.id} 
                campaign={c} 
                allModules={modules} 
                assignments={assignments} 
                learnerName={learnerFullName}
                onPlayModule={(campaign, module, assignment) => setPlaySession({ campaign, module, assignment })}
              />
            ))
          )}
        </div>
      )}

      {isHistoryTab && (
        <div className="space-y-8 animate-in fade-in duration-300">
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <SafeIcon icon={FiAward} className="text-emerald-500" /> Earned Badges
            </h3>
            {badgeAssignments.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400 shadow-sm">
                <SafeIcon icon={FiAward} className="text-4xl mx-auto mb-2 opacity-30" />
                <p>No badges earned yet. Complete modules to earn badges!</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {badgeAssignments.map((a, index) => {
                  const m = modules.find(mod => String(mod.id) === String(a.module_id));
                  if (!m) return null;
                  const isPerfect = a.score === 100;
                  const isPassed = a.score >= 80;
                  
                  return (
                    <div key={`${a.id}-${index}`} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col items-center text-center group cursor-default relative">
                      {isPassed && (
                        <button 
                          onClick={() => generateCertificate(learnerFullName, m.title, new Date().toLocaleDateString())}
                          className="absolute top-2 right-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 p-1.5 rounded-lg transition-colors"
                          title="Download Certificate"
                        >
                          <SafeIcon icon={FiDownload} />
                        </button>
                      )}
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 shadow-inner transition-transform group-hover:scale-105 ${isPerfect ? 'bg-amber-100 text-amber-500' : isPassed ? 'bg-emerald-100 text-emerald-500' : 'bg-blue-100 text-blue-500'}`}>
                        <SafeIcon icon={isPerfect ? FiAward : FiCheckCircle} className="text-3xl" />
                      </div>
                      <h4 className="font-bold text-slate-800 text-sm mb-1 line-clamp-2" title={m.title}>{m.title}</h4>
                      <p className="text-xs text-slate-500 mb-4">{m.category || 'Training'}</p>
                      
                      <div className="mt-auto pt-3 border-t border-slate-100 w-full flex justify-between items-center">
                        <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Score</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${isPerfect ? 'bg-amber-100 text-amber-700' : isPassed ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                          {a.score}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <SafeIcon icon={FiTarget} className="text-orange-500" /> Phishing Simulation History
            </h3>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              {history.filter(e => e.simulationName).length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6">No phishing simulation events recorded.</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {history.filter(e => e.simulationName).map(e => (
                    <div key={e.id} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{e.simulationName}</p>
                        <p className="text-xs text-slate-400">{new Date(e.timestamp).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${e.result === 'Correctly Reported' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                          {e.result}
                        </span>
                        {e.result !== 'Correctly Reported' && !reportedSims.includes(e.id) && (
                          <button onClick={() => setReportedSims(r => [...r, e.id])}
                            className="text-xs px-2 py-1 bg-orange-50 text-orange-600 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors font-medium">
                            Report
                          </button>
                        )}
                        {reportedSims.includes(e.id) && (
                          <span className="text-xs text-emerald-600 font-semibold">✓ Reported</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {playSession && (
        <ModulePlayerModal 
          campaign={playSession.campaign}
          module={playSession.module}
          employeeId={employee.id}
          employee={employee}
          assignment={playSession.assignment}
          onClose={() => setPlaySession(null)}
          onComplete={(newAss) => handleModuleComplete(newAss)}
        />
      )}
    </motion.div>
  );
};

export default EmployeePortal;